import { getAllEntries, getItem, setAllEntries, setItem } from './storage';
import { bytesToBase64, utf8ToUint8Array, base64ToBytes } from './utils.js';
import { fetch_request } from '../service/request';
import { getMlsDeviceIdentity } from './api.js';

const BACKUP_VERSION = 2;
const PBKDF2_ITERATIONS = 310000;
const AES_KEY_BITS = 256;
const BACKUP_RESTORE_FLAG_PREFIX = 'mls:backup-restored:';
const BACKUP_APPLIED_AT_PREFIX = 'mls:backup-applied-at:';
const BACKUP_ENDPOINT = '/api/v1/mls/backup';
const BACKUP_DEBOUNCE_MS = 2500;
const BACKUP_MIN_INTERVAL_MS = 20_000;

const activeBackupSecrets = new Map();
const backupTimers = new Map();
const backupInflight = new Map();
const backupLastAttemptAt = new Map();
const backupLastSuccessAt = new Map();
const backupLastSnapshotFingerprint = new Map();
const initUsers = new Set();

const isPriorityBackupReason = (reason) =>
  reason === 'manual' || reason === 'visibility' || reason === 'pagehide';

const computeSnapshotFingerprint = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') return null;
  let source = null;
  try {
    const { created_at: _createdAt, ...stableSnapshot } = snapshot;
    source = JSON.stringify(stableSnapshot);
  } catch {
    source = null;
  }
  if (!source) return null;
  let hash = 2166136261;
  for (let i = 0; i < source.length; i += 1) {
    hash ^= source.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

const getLocalStorageSafe = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const getRestoreFlagKey = (userId) => (userId ? `${BACKUP_RESTORE_FLAG_PREFIX}${userId}` : null);
const getBackupAppliedAtKey = (userId) => (userId ? `${BACKUP_APPLIED_AT_PREFIX}${userId}` : null);

const normalizeBackupSecret = (value) => {
  if (value == null) return null;
  try {
    const stringValue = String(value).trim();
    return stringValue || null;
  } catch {
    return null;
  }
};

const setActiveBackupSecret = (userId, secret, source = 'device') => {
  if (!userId) return;
  const normalizedSecret = normalizeBackupSecret(secret);
  if (!normalizedSecret) return;
  const existing = activeBackupSecrets.get(userId);
  if (source === 'device') {
    activeBackupSecrets.set(userId, { secret: normalizedSecret, source });
    return;
  }
  if (!existing) {
    activeBackupSecrets.set(userId, { secret: normalizedSecret, source });
  }
};

const getActiveBackupSecret = (userId) => {
  if (!userId) return null;
  const entry = activeBackupSecrets.get(userId);
  return normalizeBackupSecret(entry?.secret);
};

const getActiveBackupSource = (userId) => {
  if (!userId) return null;
  const entry = activeBackupSecrets.get(userId);
  return entry?.source || null;
};

const extractBackupSecretFromDeviceIdentity = (payload) => {
  const candidates = [
    payload,
    payload?.device_identity,
    payload?.deviceIdentity,
    payload?.device,
    payload?.data,
  ].filter((entry) => entry && typeof entry === 'object');

  for (const entry of candidates) {
    const secret =
      entry.backup_secret_b64 ??
      entry.backupSecretB64 ??
      entry.backup_secret ??
      entry.backupSecret ??
      entry.seed_b64 ??
      entry.seedB64 ??
      null;
    const normalized = normalizeBackupSecret(secret);
    if (normalized) return normalized;
  }

  return null;
};

export const isBackupRestoreFlagSet = async (userId) => {
  const key = getRestoreFlagKey(userId);
  if (!key) return false;
  const ls = getLocalStorageSafe();
  if (ls) {
    try {
      const value = ls.getItem(key);
      if (value && String(value).trim()) return true;
    } catch {}
  }
  try {
    const stored = await getItem(key);
    if (stored && String(stored).trim()) return true;
  } catch {}
  return false;
};

export const clearBackupRestoreFlag = async (userId) => {
  const key = getRestoreFlagKey(userId);
  if (!key) return;
  const ls = getLocalStorageSafe();
  if (ls) {
    try {
      ls.removeItem(key);
    } catch {}
  }
  try {
    await setItem(key, null);
  } catch {}
};

const getBackupAppliedAt = async (userId) => {
  const key = getBackupAppliedAtKey(userId);
  if (!key) return null;
  const ls = getLocalStorageSafe();
  if (ls) {
    try {
      const raw = ls.getItem(key);
      const parsed = raw ? Date.parse(raw) : NaN;
      if (Number.isFinite(parsed)) return parsed;
    } catch {}
  }
  try {
    const stored = await getItem(key);
    if (stored && typeof stored === 'string') {
      const parsed = Date.parse(stored);
      if (Number.isFinite(parsed)) return parsed;
    }
  } catch {}
  return null;
};

const setBackupAppliedAt = async (userId, timestamp) => {
  const key = getBackupAppliedAtKey(userId);
  if (!key) return;
  const iso = new Date(timestamp || Date.now()).toISOString();
  const ls = getLocalStorageSafe();
  if (ls) {
    try {
      ls.setItem(key, iso);
    } catch {}
  }
  try {
    await setItem(key, iso);
  } catch {}
};

const setBackupRestoreFlag = async (userId) => {
  const key = getRestoreFlagKey(userId);
  if (!key) return;
  const ls = getLocalStorageSafe();
  if (ls) {
    try {
      ls.setItem(key, '1');
    } catch {}
  }
  try {
    await setItem(key, '1');
  } catch {}
};

const listLocalStorageEntries = () => {
  const ls = getLocalStorageSafe();
  if (!ls) return {};
  const entries = {};
  try {
    for (let i = 0; i < ls.length; i += 1) {
      const key = ls.key(i);
      if (!key) continue;
      if (!key.startsWith('mls:') && !key.startsWith('nettgrav:mls_')) continue;
      try {
        entries[key] = ls.getItem(key);
      } catch {}
    }
  } catch {}
  return entries;
};

const restoreLocalStorageEntries = (entries = {}) => {
  const ls = getLocalStorageSafe();
  if (!ls) return;
  Object.entries(entries || {}).forEach(([key, value]) => {
    if (!key) return;
    if (!key.startsWith('mls:') && !key.startsWith('nettgrav:mls_')) return;
    try {
      if (value == null) {
        ls.removeItem(key);
      } else {
        ls.setItem(key, String(value));
      }
    } catch {}
  });
};

const deriveAesKey = async (password, saltBytes, iterations = PBKDF2_ITERATIONS) => {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    utf8ToUint8Array(String(password)),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBytes,
      iterations,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: AES_KEY_BITS },
    false,
    ['encrypt', 'decrypt'],
  );
};

const decodeUtf8 = (bytes) => {
  if (typeof TextDecoder !== 'undefined') {
    try {
      return new TextDecoder().decode(bytes);
    } catch {}
  }
  let result = '';
  for (let i = 0; i < bytes.length; i += 1) {
    result += String.fromCharCode(bytes[i]);
  }
  try {
    return decodeURIComponent(escape(result));
  } catch {
    return result;
  }
};

const importAesKeyFromBytes = async (rawKeyBytes, usages = ['encrypt', 'decrypt']) =>
  crypto.subtle.importKey(
    'raw',
    rawKeyBytes,
    { name: 'AES-GCM', length: AES_KEY_BITS },
    false,
    usages,
  );

const encryptBytesWithAesKey = async (key, plaintextBytes, ivBytes) => {
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: ivBytes }, key, plaintextBytes);
  return new Uint8Array(ciphertext);
};

const decryptBytesWithAesKey = async (key, ciphertextBytes, ivBytes) => {
  const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ivBytes }, key, ciphertextBytes);
  return new Uint8Array(plaintext);
};

const encryptSnapshot = async (password, snapshot) => {
  // Generate a random Backup Encryption Key (BEK) and wrap it with a password-derived key.
  // This enables future passwordless wrappers (passkey/device) without changing snapshot crypto.
  const bekBytes = crypto.getRandomValues(new Uint8Array(32));
  const bek = await importAesKeyFromBytes(bekBytes);

  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const wrapIv = crypto.getRandomValues(new Uint8Array(12));
  const wrappingKey = await deriveAesKey(password, salt, PBKDF2_ITERATIONS);
  const plaintext = utf8ToUint8Array(JSON.stringify(snapshot));
  const ciphertextBytes = await encryptBytesWithAesKey(bek, plaintext, iv);
  const wrappedBekBytes = await encryptBytesWithAesKey(wrappingKey, bekBytes, wrapIv);

  return {
    encrypted_blob_b64: bytesToBase64(ciphertextBytes),
    salt_b64: bytesToBase64(salt),
    iv_b64: bytesToBase64(iv),
    bek_wrapped_b64: bytesToBase64(wrappedBekBytes),
    bek_wrap_iv_b64: bytesToBase64(wrapIv),
    kdf_params: {
      name: 'PBKDF2',
      hash: 'SHA-256',
      iterations: PBKDF2_ITERATIONS,
      length: AES_KEY_BITS,
    },
    version: BACKUP_VERSION,
  };
};

const coerceBackupPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.backup && typeof payload.backup === 'object') return payload.backup;
  if (payload.snapshot && typeof payload.snapshot === 'object') return payload.snapshot;
  if (payload.data && typeof payload.data === 'object') {
    if (payload.data.backup && typeof payload.data.backup === 'object') return payload.data.backup;
    if (payload.data.snapshot && typeof payload.data.snapshot === 'object') return payload.data.snapshot;
    return payload.data;
  }
  return payload;
};

const isLegacySnapshotShape = (payload) =>
  Boolean(payload && typeof payload === 'object' && (payload.idb || payload.localStorage));

const hasEncryptedBackupFields = (payload) =>
  Boolean(
    payload &&
      typeof payload === 'object' &&
      typeof payload.encrypted_blob_b64 === 'string' &&
      payload.encrypted_blob_b64 &&
      typeof payload.salt_b64 === 'string' &&
      payload.salt_b64 &&
      typeof payload.iv_b64 === 'string' &&
      payload.iv_b64,
  );

const hasWrappedBekFields = (payload) =>
  Boolean(
    payload &&
      typeof payload === 'object' &&
      typeof payload.bek_wrapped_b64 === 'string' &&
      payload.bek_wrapped_b64 &&
      typeof payload.bek_wrap_iv_b64 === 'string' &&
      payload.bek_wrap_iv_b64,
  );

const decryptSnapshot = async (password, payload) => {
  const normalized = coerceBackupPayload(payload);
  if (!normalized) {
    return null;
  }

  // Backward compatibility: earlier backups may have stored plain snapshot objects.
  if (isLegacySnapshotShape(normalized)) {
    return normalized;
  }

  // Treat malformed payload as "no usable backup" instead of a hard restore failure.
  if (!hasEncryptedBackupFields(normalized)) {
    return null;
  }
  try {
    const iterations = Number(normalized?.kdf_params?.iterations || PBKDF2_ITERATIONS);
    const salt = base64ToBytes(normalized.salt_b64);
    const iv = base64ToBytes(normalized.iv_b64);
    const ciphertext = base64ToBytes(normalized.encrypted_blob_b64);

    // New format: wrapped BEK + password-derived wrapping key.
    if (hasWrappedBekFields(normalized)) {
      const wrappingKey = await deriveAesKey(password, salt, iterations);
      const wrapIv = base64ToBytes(normalized.bek_wrap_iv_b64);
      const wrappedBek = base64ToBytes(normalized.bek_wrapped_b64);
      const bekBytes = await decryptBytesWithAesKey(wrappingKey, wrappedBek, wrapIv);
      const bek = await importAesKeyFromBytes(bekBytes);
      const plaintext = await decryptBytesWithAesKey(bek, ciphertext, iv);
      const decoded = decodeUtf8(plaintext);
      return JSON.parse(decoded);
    }

    // Legacy format: password-derived key directly decrypts snapshot.
    const key = await deriveAesKey(password, salt, iterations);
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
    const decoded = decodeUtf8(new Uint8Array(plaintext));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

const hasLocalMlsState = async (userId) => {
  if (!userId) return true;
  const groupsKey = `mls:groups:${userId}`;
  try {
    const groups = await getItem(groupsKey);
    if (groups && typeof groups === 'object' && Object.keys(groups || {}).length > 0) {
      return true;
    }
  } catch {}
  return false;
};

const fetchBackup = async () => {
  const response = await fetch_request(BACKUP_ENDPOINT, { method: 'get' });
  if (!response?.ok) return null;
  const data = await response.json();
  return data?.backup || data || null;
};

const postBackup = async (payload) => {
  const response = await fetch_request(BACKUP_ENDPOINT, {
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return response?.ok;
};

const buildSnapshot = async () => {
  const idb = await getAllEntries();
  const localStorage = listLocalStorageEntries();
  return {
    version: BACKUP_VERSION,
    created_at: new Date().toISOString(),
    idb,
    localStorage,
  };
};

const applySnapshot = async (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') return false;
  if (snapshot.idb && typeof snapshot.idb === 'object') {
    await setAllEntries(snapshot.idb);
  }
  if (snapshot.localStorage && typeof snapshot.localStorage === 'object') {
    restoreLocalStorageEntries(snapshot.localStorage);
  }
  return true;
};

const ensureBackupInit = (userId) => {
  if (initUsers.has(userId)) return;
  initUsers.add(userId);
  if (typeof window === 'undefined') return;
  try {
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        scheduleMlsBackup(userId, 'visibility');
      }
    });
    window.addEventListener('pagehide', () => scheduleMlsBackup(userId, 'pagehide'));
  } catch {}
};

export const scheduleMlsBackup = (userId, reason = 'debounce') => {
  if (!userId || !getActiveBackupSecret(userId)) return;
  const now = Date.now();
  const lastAttemptAt = backupLastAttemptAt.get(userId) || 0;
  if (!isPriorityBackupReason(reason) && now - lastAttemptAt < BACKUP_MIN_INTERVAL_MS) {
    return;
  }
  if (backupTimers.has(userId)) return;
  const timer = setTimeout(async () => {
    backupTimers.delete(userId);
    await performMlsBackup(userId, reason);
  }, BACKUP_DEBOUNCE_MS);
  backupTimers.set(userId, timer);
};

export const performMlsBackup = async (userId, reason = 'manual') => {
  if (!userId) return false;
  if (typeof crypto === 'undefined' || !crypto?.subtle) return false;
  const secret = getActiveBackupSecret(userId);
  if (!secret) return false;
  const now = Date.now();
  const lastAttemptAt = backupLastAttemptAt.get(userId) || 0;
  if (!isPriorityBackupReason(reason) && now - lastAttemptAt < BACKUP_MIN_INTERVAL_MS) {
    return false;
  }
  if (backupInflight.get(userId)) return false;
  backupLastAttemptAt.set(userId, now);
  backupInflight.set(userId, true);
  try {
    const snapshot = await buildSnapshot();
    const snapshotFingerprint = computeSnapshotFingerprint(snapshot);
    const lastSnapshotFingerprint = backupLastSnapshotFingerprint.get(userId) || null;
    const lastSuccessAt = backupLastSuccessAt.get(userId) || 0;
    if (
      !isPriorityBackupReason(reason) &&
      snapshotFingerprint &&
      lastSnapshotFingerprint &&
      snapshotFingerprint === lastSnapshotFingerprint &&
      now - lastSuccessAt < 5 * BACKUP_MIN_INTERVAL_MS
    ) {
      return true;
    }
    const payload = await encryptSnapshot(secret, snapshot);
    const ok = await postBackup(payload);
    if (ok) {
      backupLastSuccessAt.set(userId, Date.now());
      if (snapshotFingerprint) {
        backupLastSnapshotFingerprint.set(userId, snapshotFingerprint);
      }
    }
    return ok;
  } catch (error) {
    console.warn('[MLS] backup failed', error);
    return false;
  } finally {
    backupInflight.delete(userId);
  }
};

const initMlsBackupWithSecret = async ({
  userId,
  secret,
  forceRestore = false,
  source = 'password',
} = {}) => {
  if (!userId || !secret) return false;
  if (typeof crypto === 'undefined' || !crypto?.subtle) return false;
  setActiveBackupSecret(userId, secret, source);
  ensureBackupInit(userId);

  try {
    // Establish the durable device id before reading or writing a backup. The
    // private backup secret remains client-only; this call only registers the
    // public device/client identity used to scope writes.
    await getMlsDeviceIdentity().catch(() => null);
    const backup = await fetchBackup();
    if (backup) {
      const alreadyHasState = await hasLocalMlsState(userId);
      const lastAppliedAt = await getBackupAppliedAt(userId);
      const backupUpdatedAt = backup?.updated_at ? Date.parse(backup.updated_at) : NaN;
      const shouldRestore =
        Boolean(forceRestore) ||
        !alreadyHasState ||
        (!Number.isNaN(backupUpdatedAt) && (!lastAppliedAt || backupUpdatedAt > lastAppliedAt));

      if (shouldRestore) {
        const snapshot = await decryptSnapshot(secret, backup);
        const applied = snapshot ? await applySnapshot(snapshot) : false;
        if (applied) {
          await setBackupRestoreFlag(userId);
          await setBackupAppliedAt(userId, backupUpdatedAt || Date.now());
          try {
            if (typeof console !== 'undefined' && typeof console.info === 'function') {
              console.info('[MLS] backup restore applied', {
                userId,
                backupUpdatedAt: backupUpdatedAt || null,
              });
            }
          } catch {}
        }
      }
    }
  } catch (error) {
    const message = String(error?.message || '').toLowerCase();
    const isMalformedBackup =
      message.includes('missing encryption fields') ||
      message.includes('invalidcharactererror');
    if (!isMalformedBackup) {
      console.warn('[MLS] backup restore failed', error);
    }
  }

  scheduleMlsBackup(userId, 'init');
  return true;
};

export const initMlsPasswordBackup = async ({ userId, password, forceRestore = false } = {}) =>
  initMlsBackupWithSecret({
    userId,
    secret: normalizeBackupSecret(password),
    forceRestore,
    source: 'password',
  });

export const initMlsDeviceBackup = async ({ userId, forceRestore = false } = {}) => {
  if (!userId) return false;
  let payload = null;
  try {
    payload = await getMlsDeviceIdentity();
  } catch {
    return false;
  }

  const secret = extractBackupSecretFromDeviceIdentity(payload);
  if (!secret) return false;

  return initMlsBackupWithSecret({
    userId,
    secret,
    forceRestore,
    source: 'device',
  });
};

export const clearMlsBackupSecret = (userId) => {
  if (!userId) return;
  activeBackupSecrets.delete(userId);
  backupLastAttemptAt.delete(userId);
  backupLastSuccessAt.delete(userId);
  backupLastSnapshotFingerprint.delete(userId);
  const timer = backupTimers.get(userId);
  if (timer) {
    clearTimeout(timer);
    backupTimers.delete(userId);
  }
};

export const clearMlsBackupPassword = clearMlsBackupSecret;
